import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Brain, Hammer, Shield, Swords, Target } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import {
  useCoachArmyMutation,
  useCoachUpgradePriorityMutation,
  useCoachWarAttackMutation,
  useGetCocLinkQuery,
} from '@/services/qclansApi';
import {
  classifyQclansError,
  qclansErrorKey,
  retryAfterSeconds,
} from '@/services/qclansErrors';
import type { CoachResponse } from '@/types/qclans';
import { cn } from '@/lib/cn';

type Tool = 'upgrade-priority' | 'army' | 'war-attack';

/**
 * AI Coach (`POST /v1/coach/{upgrade-priority,army,war-attack}`). Three tools,
 * each rendering the returned `advice` markdown-ish text.
 *
 * Honest constraints + error handling per the contract:
 *   - 503 `ai_not_configured` → the whole coach is DISABLED (the API has no
 *     Anthropic key). We show a single "unavailable" panel and hide the tools.
 *   - 429 `rate_limited` → show the rate-limit notice with the Retry-After.
 *   - Coverage is army / upgrade / war-attack only — there is NO base-layout
 *     review (the CoC API doesn't expose layouts), stated in the subtitle copy.
 */
export function CocCoach() {
  const { t } = useTranslation('coc');
  const haptics = useHaptics();

  const linkQuery = useGetCocLinkQuery();
  const accounts = linkQuery.data?.accounts ?? [];

  const [activeTag, setActiveTag] = useState<string | null>(null);
  useEffect(() => {
    if (!activeTag && accounts.length > 0) setActiveTag(accounts[0].playerTag);
  }, [accounts, activeTag]);

  const [tool, setTool] = useState<Tool>('upgrade-priority');
  const [goal, setGoal] = useState('');
  const [defenderTag, setDefenderTag] = useState('');

  const [runUpgrade, upgradeState] = useCoachUpgradePriorityMutation();
  const [runArmy, armyState] = useCoachArmyMutation();
  const [runWar, warState] = useCoachWarAttackMutation();

  const [advice, setAdvice] = useState<CoachResponse | null>(null);
  const [errorKind, setErrorKind] = useState<ReturnType<typeof classifyQclansError> | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);

  const isLoading = upgradeState.isLoading || armyState.isLoading || warState.isLoading;
  // The coach is disabled service-wide when any tool returns 503.
  const aiDisabled = errorKind === 'ai_not_configured';

  const run = async () => {
    if (!activeTag) return;
    haptics.impact('medium');
    setAdvice(null);
    setErrorKind(null);
    setRetryAfter(null);
    try {
      let res: CoachResponse;
      if (tool === 'upgrade-priority') {
        res = await runUpgrade({ playerTag: activeTag }).unwrap();
      } else if (tool === 'army') {
        res = await runArmy({ playerTag: activeTag, goal: goal.trim() || undefined }).unwrap();
      } else {
        const def = defenderTag.trim();
        if (!def) return;
        res = await runWar({ attackerTag: activeTag, defenderTag: def }).unwrap();
      }
      haptics.notification('success');
      setAdvice(res);
    } catch (err) {
      haptics.notification('error');
      const kind = classifyQclansError(err);
      setErrorKind(kind);
      if (kind === 'rate_limited') setRetryAfter(retryAfterSeconds(err));
    }
  };

  if (linkQuery.isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 px-6 py-12 text-center">
        <Brain aria-hidden className="h-10 w-10 text-fg-muted" />
        <p className="text-sm text-fg-muted">{t('coach.noLinked')}</p>
        <Link
          to="/coc/link"
          className="rounded-xl bg-brand px-4 py-2.5 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
        >
          {t('coach.linkCta')}
        </Link>
      </div>
    );
  }

  const tools: { id: Tool; icon: React.ReactNode; title: string; sub: string }[] = [
    {
      id: 'upgrade-priority',
      icon: <Hammer className="h-5 w-5" strokeWidth={2.5} />,
      title: t('coach.tools.upgradePriority'),
      sub: t('coach.tools.upgradePrioritySub'),
    },
    {
      id: 'army',
      icon: <Swords className="h-5 w-5" strokeWidth={2.5} />,
      title: t('coach.tools.army'),
      sub: t('coach.tools.armySub'),
    },
    {
      id: 'war-attack',
      icon: <Target className="h-5 w-5" strokeWidth={2.5} />,
      title: t('coach.tools.warAttack'),
      sub: t('coach.tools.warAttackSub'),
    },
  ];

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Brain aria-hidden className="h-5 w-5" />
          </span>
          <h1 className="text-2xl font-bold text-fg">{t('coach.title')}</h1>
        </div>
        <p className="text-sm text-fg-muted">{t('coach.subtitle')}</p>
      </header>

      {aiDisabled ? (
        /* Service-wide 503 — the coach is unavailable; hide tools + inputs. */
        <div className="flex flex-col items-center gap-3 rounded-xl bg-bg-secondary p-5 text-center">
          <AlertTriangle aria-hidden className="h-8 w-8 text-warning" />
          <p className="text-sm font-semibold text-fg">{t('coach.disabledTitle')}</p>
          <p className="text-[12px] leading-snug text-fg-muted">{t('coach.disabledBody')}</p>
        </div>
      ) : (
        <>
          {/* Account switcher */}
          {accounts.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {accounts.map((acc) => (
                <button
                  key={acc.playerTag}
                  type="button"
                  onClick={() => {
                    haptics.selection();
                    setActiveTag(acc.playerTag);
                  }}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
                    acc.playerTag === activeTag
                      ? 'bg-brand text-brand-fg'
                      : 'bg-bg-tertiary text-fg-muted',
                  )}
                >
                  {acc.playerName}
                </button>
              ))}
            </div>
          )}

          {/* Tool picker */}
          <div className="flex flex-col gap-2">
            {tools.map((tl) => (
              <button
                key={tl.id}
                type="button"
                onClick={() => {
                  haptics.selection();
                  setTool(tl.id);
                  setAdvice(null);
                  setErrorKind(null);
                }}
                className={cn(
                  'flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors',
                  tl.id === tool
                    ? 'border-brand bg-brand/10'
                    : 'border-border bg-bg-secondary',
                )}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand">
                  {tl.icon}
                </span>
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-fg">{tl.title}</span>
                  <span className="text-[11px] text-fg-muted">{tl.sub}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Tool-specific inputs */}
          {tool === 'army' && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {t('coach.goalLabel')}
              </span>
              <input
                type="text"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder={t('coach.goalPlaceholder')}
                className="rounded-xl border border-border bg-bg-secondary px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted focus:border-brand focus:outline-none"
              />
            </label>
          )}
          {tool === 'war-attack' && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {t('coach.defenderLabel')}
              </span>
              <input
                type="text"
                autoCapitalize="characters"
                value={defenderTag}
                onChange={(e) => setDefenderTag(e.target.value)}
                placeholder={t('coach.defenderPlaceholder')}
                className="rounded-xl border border-border bg-bg-secondary px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted focus:border-brand focus:outline-none"
              />
            </label>
          )}

          <button
            type="button"
            onClick={() => void run()}
            disabled={isLoading || (tool === 'war-attack' && !defenderTag.trim())}
            className="flex items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
          >
            {isLoading ? t('coach.running') : advice ? t('coach.again') : t('coach.run')}
          </button>

          {/* Rate-limit notice */}
          {errorKind === 'rate_limited' && (
            <div className="flex flex-col gap-1 rounded-xl bg-warning/15 px-4 py-3" role="alert">
              <p className="text-sm font-semibold text-warning">{t('coach.rateLimitedTitle')}</p>
              <p className="text-[12px] text-fg-muted">
                {retryAfter != null
                  ? t('coach.rateLimitedRetry', { seconds: retryAfter })
                  : t('coach.rateLimitedBody')}
              </p>
            </div>
          )}

          {/* Other errors */}
          {errorKind && errorKind !== 'rate_limited' && (
            <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
              {t(qclansErrorKey(errorKind))}
            </p>
          )}

          {/* Advice */}
          {advice && (
            <section className="flex flex-col gap-2 rounded-xl bg-bg-secondary p-4">
              <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-fg-muted">
                <Shield className="h-3.5 w-3.5" />
                {t('coach.adviceTitle')}
              </h2>
              <CoachAdvice text={advice.advice} />
              <p className="text-[10px] text-fg-muted">
                {t('coach.modelNote', { model: advice.model })}
              </p>
            </section>
          )}
        </>
      )}

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}

/**
 * Render the coach's markdown-ish `advice` as plain paragraphs / bullets. We
 * intentionally do NOT pull in a full markdown renderer — the advice is short
 * structured text; splitting on blank lines + bullet prefixes is enough and
 * keeps the bundle lean.
 */
function CoachAdvice({ text }: { text: string }) {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  return (
    <div className="flex flex-col gap-1.5 text-sm leading-relaxed text-fg">
      {lines.map((line, i) => {
        const bullet = /^[-*•]\s+/.test(line);
        const heading = /^#{1,6}\s+/.test(line);
        if (heading) {
          return (
            <p key={i} className="mt-1 font-bold text-fg">
              {line.replace(/^#{1,6}\s+/, '')}
            </p>
          );
        }
        if (bullet) {
          return (
            <p key={i} className="flex gap-2 pl-1">
              <span className="text-brand">•</span>
              <span>{line.replace(/^[-*•]\s+/, '')}</span>
            </p>
          );
        }
        return <p key={i}>{line}</p>;
      })}
    </div>
  );
}
